import Phaser from 'phaser';
import { SCENES } from '../config/constants';
import { DISTRICTS, type District } from '../data/districts';
import { gameStore } from '../simulation/GameStore';
import { ParkingLotEngine } from '../simulation/ParkingLotEngine';
import { ReputationEngine } from '../simulation/ReputationEngine';
import { formatCurrency, formatCurrencyCompact } from '../utils/formatCurrency';
import { fontPx, px } from '../ui/scale';
import { t, tDistrict } from '../ui/i18n';

/**
 * CityScene (spec sections 27/45): the city map. Each district shows either a
 * "build lot" action or a summary of the lot already there. Clicking opens the
 * ParkingLotScene for that lot.
 *
 * The scene only renders from / sends intent to the GameStore; it computes no
 * game logic (spec sections 1 & 42). The persistent UIScene owns the sim loop.
 */
export class CityScene extends Phaser.Scene {
  private marketPanel?: Phaser.GameObjects.Container;

  constructor() {
    super(SCENES.CITY);
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#101826');

    // Keep the HUD overlay running across city <-> lot transitions.
    if (!this.scene.isActive(SCENES.UI)) {
      this.scene.launch(SCENES.UI);
    }

    this.drawCity();
  }

  private drawCity(): void {
    const { width } = this.scale;
    const cx = width / 2;

    this.add
      .text(cx, px(80), t('city.title'), {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(40),
        color: '#eceff1',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(cx, px(80) + px(34), t('city.hint'), {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(15),
        color: '#90a4ae',
      })
      .setOrigin(0.5);

    // Player's overall market share + a button to see the full breakdown.
    const share = Math.round(gameStore.getPlayerMarketShare() * 100);
    this.add
      .text(cx, px(80) + px(58), t('city.share', { v: share }), {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(15),
        color: '#ffd54f',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    // Campaign objective progress / win banner (spec section 38).
    const val = gameStore.getValuation();
    if (gameStore.isObjectiveAchieved()) {
      this.add
        .text(cx, px(80) + px(80), t('city.goalReached'), {
          fontFamily: 'Segoe UI, Tahoma, sans-serif',
          fontSize: fontPx(16),
          color: '#a5d6a7',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
    } else {
      const valPct = Math.min(100, Math.round((val.total / 100_000_000) * 100));
      this.add
        .text(
          cx,
          px(80) + px(80),
          t('city.goal', { v: formatCurrencyCompact(val.total), p: valPct, s: share }),
          {
            fontFamily: 'Segoe UI, Tahoma, sans-serif',
            fontSize: fontPx(13),
            color: '#b0bec5',
          }
        )
        .setOrigin(0.5);
    }

    // Active events banner (spec sections 22/24).
    const active = gameStore.getActiveEvents();
    if (active.length > 0) {
      const names = active.map((e) => e.name).join('  •  ');
      this.add
        .text(cx, px(80) + px(104), t('city.active', { v: names }), {
          fontFamily: 'Segoe UI, Tahoma, sans-serif',
          fontSize: fontPx(13),
          color: '#80deea',
        })
        .setOrigin(0.5);
    }

    const mbW = px(160);
    const mbH = px(40);
    const mbx = this.scale.width - px(24) - mbW / 2;
    const mby = px(72);
    const mb = this.add
      .rectangle(mbx, mby, mbW, mbH, 0x37474f)
      .setStrokeStyle(px(2), 0x90a4ae)
      .setInteractive({ useHandCursor: true });
    this.add
      .text(mbx, mby, t('city.market'), {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(15),
        color: '#eceff1',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    mb.on('pointerup', () => this.toggleMarket());

    const cardW = px(200);
    const cardH = px(172);
    const gap = px(24);
    const total = DISTRICTS.length * cardW + (DISTRICTS.length - 1) * gap;
    let x = cx - total / 2 + cardW / 2;
    const y = this.scale.height / 2;

    for (const district of DISTRICTS) {
      this.drawDistrictCard(district, x, y, cardW, cardH);
      x += cardW + gap;
    }
  }

  private drawDistrictCard(
    district: District,
    x: number,
    y: number,
    w: number,
    h: number
  ): void {
    const lot = gameStore.getLotByDistrict(district.id);

    const card = this.add
      .rectangle(x, y, w, h, district.color, 0.85)
      .setStrokeStyle(px(2), 0xffffff, 0.25)
      .setInteractive({ useHandCursor: true });

    this.add
      .text(x, y - h / 2 + px(22), tDistrict(district.id, district.name), {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(20),
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    if (lot) {
      const capacity = ParkingLotEngine.getCapacity(lot);
      this.add
        .text(
          x,
          y + px(6),
          `Occupancy: ${lot.occupiedSpaces}/${capacity}\nRep: ${Math.round(
            lot.reputation
          )} (${ReputationEngine.rating(lot.reputation)})\nEntrance: ${
            ParkingLotEngine.hasEntrance(lot) ? '✓' : '—'
          }  Exit: ${ParkingLotEngine.hasExit(lot) ? '✓' : '—'}`,
          {
            fontFamily: 'Segoe UI, Tahoma, sans-serif',
            fontSize: fontPx(13),
            color: '#ffffff',
            align: 'center',
          }
        )
        .setOrigin(0.5);
      // City overview: this branch's lifetime revenue & profit (spec §37).
      const profitColor = lot.profit >= 0 ? '#a5d6a7' : '#ef9a9a';
      this.add
        .text(
          x,
          y + h / 2 - px(34),
          `Rev ${formatCurrencyCompact(lot.revenue)}`,
          {
            fontFamily: 'Segoe UI, Tahoma, sans-serif',
            fontSize: fontPx(12),
            color: '#e0e0e0',
          }
        )
        .setOrigin(0.5);
      this.add
        .text(
          x,
          y + h / 2 - px(20),
          `P/L ${formatCurrencyCompact(lot.profit)}`,
          {
            fontFamily: 'Segoe UI, Tahoma, sans-serif',
            fontSize: fontPx(12),
            color: profitColor,
            fontStyle: 'bold',
          }
        )
        .setOrigin(0.5);
      this.add
        .text(x, y + h / 2 - px(6), t('city.manage'), {
          fontFamily: 'Segoe UI, Tahoma, sans-serif',
          fontSize: fontPx(13),
          color: '#fff9c4',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);

      card.on('pointerup', () => this.openLot(lot.id));
    } else {
      this.add
        .text(x, y + px(2), t('city.land', { v: formatCurrency(district.landPrice) }), {
          fontFamily: 'Segoe UI, Tahoma, sans-serif',
          fontSize: fontPx(14),
          color: '#ffffff',
        })
        .setOrigin(0.5);
      this.add
        .text(x, y + h / 2 - px(18), t('city.build'), {
          fontFamily: 'Segoe UI, Tahoma, sans-serif',
          fontSize: fontPx(15),
          color: '#c8e6c9',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);

      card.on('pointerup', () => this.tryBuildLot(district));
    }
  }

  private toggleMarket(): void {
    if (this.marketPanel) {
      this.marketPanel.destroy();
      this.marketPanel = undefined;
      return;
    }
    this.buildMarketOverlay();
  }

  private buildMarketOverlay(): void {
    const shares = gameStore.getMarketShares();
    const competitors = gameStore.getCompetitors();
    const { width, height } = this.scale;

    const panel = this.add.container(0, 0).setDepth(100);
    const dim = this.add
      .rectangle(0, 0, width, height, 0x000000, 0.55)
      .setOrigin(0)
      .setInteractive();
    dim.on('pointerup', () => this.toggleMarket());
    panel.add(dim);

    const pw = px(600);
    const ph = px(120) + shares.length * px(46) + competitors.length * px(40);
    const cx = width / 2;
    const top = height / 2 - ph / 2;
    panel.add(
      this.add.rectangle(cx, height / 2, pw, ph, 0x141c2b).setStrokeStyle(px(2), 0x5c6bc0)
    );
    panel.add(
      this.add
        .text(cx, top + px(28), 'MARKET SHARE', {
          fontFamily: 'Segoe UI, Tahoma, sans-serif',
          fontSize: fontPx(22),
          color: '#eceff1',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
    );

    const colors: Record<string, number> = {
      player: 0xffd54f,
      budget: 0x66bb6a,
      premium: 0xef5350,
      smartpark: 0x42a5f5,
    };
    const leftX = cx - pw / 2 + px(28);
    const barX = leftX + px(150);
    const barMax = pw - px(150) - px(56);
    let y = top + px(66);

    for (const s of shares) {
      const color = colors[s.ownerId] ?? 0x90a4ae;
      panel.add(
        this.add
          .text(leftX, y, s.ownerName, {
            fontFamily: 'Segoe UI, Tahoma, sans-serif',
            fontSize: fontPx(15),
            color: '#eceff1',
            fontStyle: s.ownerId === 'player' ? 'bold' : 'normal',
          })
          .setOrigin(0, 0.5)
      );
      panel.add(
        this.add.rectangle(barX, y, barMax, px(16), 0x263238).setOrigin(0, 0.5)
      );
      panel.add(
        this.add
          .rectangle(barX, y, Math.max(1, barMax * s.share), px(16), color)
          .setOrigin(0, 0.5)
      );
      panel.add(
        this.add
          .text(barX + barMax + px(8), y, `${Math.round(s.share * 100)}%`, {
            fontFamily: 'Segoe UI, Tahoma, sans-serif',
            fontSize: fontPx(13),
            color: '#eceff1',
          })
          .setOrigin(0, 0.5)
      );
      y += px(46);
    }

    y += px(6);
    for (const c of competitors) {
      const priceLo = Math.min(...c.lots.map((l) => l.price));
      const priceHi = Math.max(...c.lots.map((l) => l.price));
      const cap = c.lots.reduce((n, l) => n + l.capacity, 0);
      panel.add(
        this.add
          .text(
            leftX,
            y,
            `${c.name}  •  ฿${priceLo}-${priceHi}/hr  •  ${c.lots.length} lots (${cap} spaces)  •  Rep ${Math.round(c.reputation)}`,
            {
              fontFamily: 'Segoe UI, Tahoma, sans-serif',
              fontSize: fontPx(12),
              color: '#b0bec5',
            }
          )
          .setOrigin(0, 0.5)
      );
      y += px(40);
    }

    this.marketPanel = panel;
  }

  private tryBuildLot(district: District): void {
    const result = gameStore.buildLot(district.id);
    if (result.ok) {
      this.openLot(result.lotId);
    } else if (result.reason === 'insufficient-funds') {
      this.toast(t('city.noCash', { d: tDistrict(district.id, district.name) }));
    } else if (result.reason === 'already-exists') {
      this.toast(t('city.exists', { d: tDistrict(district.id, district.name) }));
    }
  }

  private openLot(lotId: string): void {
    this.scene.start(SCENES.PARKING_LOT, { lotId });
  }

  private toast(message: string): void {
    const { width, height } = this.scale;
    const msg = this.add
      .text(width / 2, height * 0.85, message, {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(16),
        color: '#ffcdd2',
        backgroundColor: '#3a1a1a',
        padding: { x: px(14), y: px(8) },
      })
      .setOrigin(0.5)
      .setAlpha(0);

    this.tweens.add({
      targets: msg,
      alpha: 1,
      duration: 150,
      yoyo: true,
      hold: 1400,
      onComplete: () => msg.destroy(),
    });
  }
}
